/**
 * Arc context resolution utilities
 * Resolves arc-derived values and their sources for display
 */

import { Sphere } from 'types/global';
import { ArcTemplate, KeyDateDefinition } from 'types/arc';
import { needId } from '@center-platform/arcs/needs';

export interface ArcContextField {
  key: string;
  label: string;
  /** `null` marks a vacancy — a key date with no resolvable value (see `needId`). */
  value: any;
  displayValue: string;
  source: ArcContextSource;
  editable: boolean;
  /** Set only on a vacancy: the `@center-platform/arcs/needs` need id whose answer would fill it. */
  needId?: string;
}

export interface ArcContextSource {
  type: 'property' | 'calculated' | 'reference' | 'rule' | 'anchor';
  detail: string;
  linkTo?: {
    type: 'sphere' | 'property' | 'template';
    id?: string;
    name?: string;
    perspective?: string;
  };
  formula?: string;
}

/**
 * Resolve flow context fields from sphere and template
 * Identifies all values derived from the arc and their sources
 */
export async function resolveArcContext(
  arcSphere: Sphere,
  parentSphere: Sphere,
  template: ArcTemplate,
  translateFn: (key: string, params?: any) => string,
  graphClient?: any
): Promise<ArcContextField[]> {
  const fields: ArcContextField[] = [];

  // 1. Key dates from anchors
  if (template.definition.keyDates) {
    for (const [key, dateDef] of Object.entries(template.definition.keyDates)) {
      const resolution = resolveKeyDate(key, dateDef, arcSphere, parentSphere, translateFn);

      // `'needId' in` rather than `resolved === false`: a boolean discriminant does not narrow
      // the union under this package's non-strict compiler settings.
      if (!('needId' in resolution)) {
        fields.push({
          key,
          label: formatLabel(key),
          value: resolution.value,
          displayValue: formatDateValue(resolution.value, translateFn),
          source: resolution.source,
          editable: false,
        });
      } else {
        // A vacancy is surfaced, not hidden (specs/arc-model-v2.md SC5): the panel shows the key
        // date as unanswered and names the `arcs/needs` need that would fill it, so the reader
        // sees that a date is missing rather than nothing at all. `value: null` is the vacancy
        // marker — never a guessed date.
        fields.push({
          key,
          label: formatLabel(key),
          value: null,
          displayValue: translateFn('arc.context.not_configured'),
          source: {
            type: 'anchor',
            detail: dateDef.expression
              ? translateFn('arc.context.from_expression', { expression: dateDef.expression })
              : translateFn('arc.context.not_configured'),
            ...(dateDef.expression ? { linkTo: { type: 'property' as const, name: dateDef.expression } } : {}),
          },
          needId: resolution.needId,
          editable: false,
        });
      }
    }
  }

  // 2. Stage assignments (current stage assignees)
  const currentStageId = arcSphere.properties.currentStageId;
  if (currentStageId) {
    const currentStage = template.definition.stages.find(s => s.id === currentStageId);

    if (currentStage?.assignedTo) {
      const assignees = resolveAssignees(currentStage.assignedTo, parentSphere);

      if (assignees.spheres.length > 0) {
        // Get perspective name if applicable
        let perspectiveName = currentStage.assignedTo.value;
        if (currentStage.assignedTo.type === 'perspective') {
          const assignedPerspective = parentSphere.assignedPerspectives?.find(
            ap => ap.perspectiveProperties.system_name === currentStage.assignedTo.value
          );
          if (assignedPerspective) {
            perspectiveName = assignedPerspective.perspectiveProperties.name;
          }
        }

        fields.push({
          key: 'currentAssignees',
          label: 'arc.context.current_stage_assignees', // Translation key
          value: assignees.spheres,
          displayValue: assignees.displayNames.join(', '),
          source: {
            type: 'rule',
            detail: `${currentStage.assignedTo.type === 'perspective' ? translateFn('arc.context.perspective') : currentStage.assignedTo.type}: ${perspectiveName}`,
            linkTo: {
              type: 'sphere',
              perspective: currentStage.assignedTo.type === 'perspective' ? currentStage.assignedTo.value : undefined,
            },
          },
          editable: false,
        });
      }
    }
  }

  // 3. Arc-derived properties that appear on parent sphere
  // Look for properties that might be calculated or referenced from arc
  const arcDerivedProperties = extractArcDerivedProperties(parentSphere, template, translateFn);
  for (const prop of arcDerivedProperties) {
    fields.push(prop);
  }

  return fields;
}

/** The result of resolving a key date: either a live value, or a vacancy naming the
 *  `arcs/needs` need that would answer it — never a guessed fallback (EAINT-888 Phase 2). */
export type KeyDateResolution =
  | { resolved: true; value: Date; source: ArcContextSource }
  | { resolved: false; needId: string };

/**
 * Resolve a key date from its definition. An expression that doesn't resolve against the
 * current spheres is a vacancy, not a guessed date — the `FallbackDefinition.creationOffset`
 * mechanism this replaced computed a plausible-looking date with no basis in authored data.
 */
function resolveKeyDate(
  key: string,
  dateDef: KeyDateDefinition,
  arcSphere: Sphere,
  parentSphere: Sphere,
  translateFn: (key: string, params?: any) => string
): KeyDateResolution {
  if (dateDef.expression) {
    // Same two-root grammar as graph-service's `resolveAnchorExpression`: `parent.<property>`
    // reads the hosting sphere, `sphere.<property>` the arc vertex itself — both against the
    // sphere's `properties`, which is where every node property lives.
    const propValue = getNestedProperty(
      { parent: parentSphere.properties, sphere: arcSphere.properties },
      dateDef.expression
    );

    if (propValue) {
      const dateValue = new Date(Array.isArray(propValue) ? propValue[0] : propValue);

      return {
        resolved: true,
        value: dateValue,
        source: {
          type: 'anchor',
          detail: translateFn('arc.context.from_expression', { expression: dateDef.expression }),
          linkTo: {
            type: 'property',
            name: dateDef.expression,
          },
        },
      };
    }
  }

  return { resolved: false, needId: needId('key_date', key) };
}

/**
 * Resolve assignees from assignment rule
 */
function resolveAssignees(
  assignedTo: any,
  parentSphere: Sphere
): { spheres: Sphere[]; displayNames: string[] } {
  if (assignedTo.type === 'perspective') {
    // Find the assigned perspective by system_name
    const assignedPerspective = parentSphere.assignedPerspectives?.find(
      ap => ap.perspectiveProperties.system_name === assignedTo.value
    );

    if (assignedPerspective?.assigned) {
      // Extract sphere objects and names from the assigned array
      const spheres = assignedPerspective.assigned.map(a => a.sphere);
      const displayNames = spheres.map(s => {
        const name = Array.isArray(s.properties.name) ? s.properties.name[0] : s.properties.name;
        return name || s.id;
      });
      return { spheres, displayNames };
    }
  }

  if (assignedTo.type === 'user') {
    return { spheres: [], displayNames: [assignedTo.value] };
  }

  if (assignedTo.type === 'group') {
    // Group member resolution not implemented - returning group name as placeholder
    return { spheres: [], displayNames: [`Group: ${assignedTo.value}`] };
  }

  return { spheres: [], displayNames: [] };
}

/**
 * Extract properties on parent sphere that are derived from arc
 * This is a heuristic - looks for common arc-related properties
 */
function extractArcDerivedProperties(
  parentSphere: Sphere,
  template: ArcTemplate,
  translateFn: (key: string) => string
): ArcContextField[] {
  const fields: ArcContextField[] = [];

  // Check for common flow-derived properties
  const arcProps = ['dueDate', 'startDate', 'targetDate', 'deadline', 'assignedTo'];

  for (const propName of arcProps) {
    if (parentSphere.properties[propName]) {
      const value = parentSphere.properties[propName];
      const displayValue = formatPropertyValue(value, translateFn);

      // Check if this property is referenced in template
      const isReferencedInTemplate = isPropertyReferencedInTemplate(propName, template);

      if (isReferencedInTemplate) {
        fields.push({
          key: propName,
          label: formatLabel(propName),
          value,
          displayValue,
          source: {
            type: 'reference',
            detail: translateFn('arc.context.referenced_in_template'),
            linkTo: {
              type: 'template',
              name: template.system_name,
            },
          },
          editable: false,
        });
      }
    }
  }

  return fields;
}

/**
 * Check if a property is referenced in the template
 */
function isPropertyReferencedInTemplate(
  propName: string,
  template: ArcTemplate
): boolean {
  // Check keyDates - check if any keyDate uses expression that references this property
  if (template.definition.keyDates) {
    for (const dateDef of Object.values(template.definition.keyDates)) {
      if (dateDef.expression?.includes(propName)) {
        return true;
      }
    }
  }

  // Check stages for property references
  for (const stage of template.definition.stages) {
    // Check startOffsetAnchor
    if (stage.startOffsetAnchor?.includes(propName)) {
      return true;
    }
  }

  return false;
}

/**
 * Format a property name into a readable label
 */
function formatLabel(key: string): string {
  return key
    .replaceAll(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
    .trim();
}

/**
 * Format a date value for display
 */
function formatDateValue(value: Date, translateFn: (key: string) => string): string {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return translateFn('common.error.invalid_date');
  }

  return value.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format any property value for display
 */
function formatPropertyValue(value: any, translateFn: (key: string) => string): string {
  if (value === null || value === undefined) {
    return '—';
  }

  if (value instanceof Date) {
    return formatDateValue(value, translateFn);
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return '—';

    // Check if it's a date array
    if (typeof value[0] === 'string' && !Number.isNaN(Date.parse(value[0]))) {
      return formatDateValue(new Date(value[0]), translateFn);
    }

    return value.join(', ');
  }

  // Check if it's a date string
  if (typeof value === 'string' && !Number.isNaN(Date.parse(value))) {
    return formatDateValue(new Date(value), translateFn);
  }

  return String(value);
}

/**
 * Get nested property from object using dot notation
 */
export function getNestedProperty(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => current?.[key], obj);
}
